import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchWishlistData, removeWishlistEntry } from '../controllers/wishlistController';

const WishlistView = () => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadWishlistData();
  }, []);

  const loadWishlistData = async () => {
    await fetchWishlistData(setEntries, setIsLoading, setError);
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;
    
    setIsDeleting(true);
    
    try {
      const success = await removeWishlistEntry(
        entryToDelete.id, 
        entries, 
        setEntries, 
        setError
      );
      
      if (success) {
        setShowDeleteConfirm(false);
        setEntryToDelete(null);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setEntryToDelete(null);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 mx-auto mb-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-300">Loading wishlist data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-white">Wishlist Entries</h2>
        <motion.button
          onClick={loadWishlistData}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-zinc-700 text-white px-4 py-2 rounded-lg hover:bg-zinc-600 transition-colors"
        >
          Refresh
        </motion.button>
      </div>

      {error && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6"
        >
          {error}
        </motion.div>
      )}

      {entries.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800 rounded-xl">
          <p className="text-gray-400">No wishlist entries found.</p>
        </div>
      ) : (
        <div className="bg-zinc-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-zinc-700">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Email Status</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-700">
                <AnimatePresence>
                  {entries.map((entry) => (
                    <motion.tr 
                      key={entry.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="bg-zinc-800 hover:bg-zinc-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{entry.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{entry.fullName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{entry.location || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{entry.referralSource || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {entry.emailSent ? (
                          <div className="flex flex-col">
                            <span className="text-green-400 text-xs bg-green-500/20 px-2 py-1 rounded-full inline-block w-fit">
                              ✓ Sent
                            </span>
                            {entry.emailSentAt && (
                              <span className="text-gray-400 text-xs mt-1">
                                {formatDate(entry.emailSentAt)}
                              </span>
                            )}
                            {entry.emailSubject && (
                              <span className="text-gray-400 text-xs mt-1 truncate max-w-24" title={entry.emailSubject}>
                                "{entry.emailSubject}"
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs bg-gray-500/20 px-2 py-1 rounded-full">
                            Not sent
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{formatDate(entry.createdAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <motion.button
                          onClick={() => handleDeleteClick(entry)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          Delete
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-zinc-800 rounded-xl p-6 w-full max-w-md"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Confirm Deletion</h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete the wishlist entry for <span className="text-white font-medium">{entryToDelete?.email}</span>?
                This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <motion.button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isDeleting}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </span>
                  ) : "Delete"}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WishlistView; 